"use client";

import React, { useState } from "react";
import { motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';

// --- Reusable Animation Variants (Unchanged) ---
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  whileInView: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  viewport: { once: true, amount: 0.2 }
};

const staggerContainer = {
  initial: {},
  animate: { transition: { staggerChildren: 0.1 } },
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true, amount: 0.2 }
};

const slideIn = (direction = 'left') => ({
  initial: { x: direction === 'left' ? -100 : 100, opacity: 0 },
  whileInView: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 20 } },
  viewport: { once: true, amount: 0.2 }
});

const sentenceVariant = {
  animate: { transition: { delayChildren: 0.3, staggerChildren: 0.1 } },
};

const wordVariant = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const promise = fetch("https://voidapi.shigjeta.com/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
    }).then(async (res) => {
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || `An error occurred: ${res.statusText}`);
        }
        return res.json();
    });

    toast.promise(promise, {
      loading: 'Sending your message...',
      success: (data) => {
        setFormData({ name: "", email: "", subject: "", message: "" });
        setIsSubmitting(false);
        return data.message || "Thank you! Your message has been sent.";
      },
      error: (err) => {
        setIsSubmitting(false);
        return err.message || "Oops! Something went wrong. Please try again.";
      },
    });
  };

  return (
    <main className="font-sans bg-white text-gray-900 min-h-screen scroll-smooth overflow-x-hidden">
      <Toaster richColors position="bottom-right" />

      {/* Hero Section */}
      <section className="relative flex min-h-[60vh] items-center justify-center py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden opacity-20">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
            <source src="/images/Video2.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
            <circle cx="700" cy="200" r="100" fill="#16a085" fillOpacity="0.2" />
          </svg>
        </div>
        <motion.div
          className="relative z-10 w-full max-w-7xl mx-auto text-center"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.h1 
            className="text-[2.5rem] leading-tight sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight"
            variants={sentenceVariant}
          >
            <span className="block">
              {"Get In Touch".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>
                  {word} 
                </motion.span>
              ))}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"We're Here to Help".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>
                  {word} 
                </motion.span>
              ))}
            </span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="mt-4 max-w-xl md:max-w-3xl mx-auto text-md sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
            Have questions or want to learn more about Void? Send us a message, and we'll get back to you promptly.
          </motion.p>
          <motion.div variants={fadeInUp} className="mt-10">
            <motion.a
              href="#contact-form"
              className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg text-base sm:text-lg"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Contact Us Now
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Contact Form & Visuals Section */}
      <section id="contact-form" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Form */}
          <div>
            <h2 className="text-[2.25rem] leading-tight sm:text-4xl font-extrabold text-gray-900 mb-8 tracking-tight">
              Send Us a Message
            </h2>
            <form
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-gray-100"
              noValidate
            >
              <div variants={fadeInUp} className="mb-6">
                <label htmlFor="name" className="block mb-2 font-semibold text-gray-800">Name <span className="text-red-600">*</span></label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" placeholder="Your full name" />
              </div>
              <div variants={fadeInUp} className="mb-6">
                <label htmlFor="email" className="block mb-2 font-semibold text-gray-800">Email <span className="text-red-600">*</span></label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" placeholder="you@example.com" />
              </div>
              <div variants={fadeInUp} className="mb-6">
                <label htmlFor="subject" className="block mb-2 font-semibold text-gray-800">Subject</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" placeholder="Subject of your message" />
              </div>
              <div variants={fadeInUp} className="mb-6">
                <label htmlFor="message" className="block mb-2 font-semibold text-gray-800">Message <span className="text-red-600">*</span></label>
                <textarea id="message" name="message" required rows="6" value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" placeholder="Write your message here..."></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#16a085] text-white font-semibold px-8 py-4 rounded-lg shadow-lg w-full disabled:opacity-60 disabled:cursor-not-allowed"
                whileHover={{ scale: isSubmitting ? 1 : 1.02, y: isSubmitting ? 0 : -2 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
          
          {/* Visual Element */}
          <motion.div {...slideIn('right')} className="relative mt-8 lg:mt-0">
            <img src="/images/Contact.png" alt="An illustration of a support agent assisting a customer" className="rounded-xl shadow-lg w-full transform hover:scale-[1.02] transition-transform duration-300" />
            <div className="absolute inset-0 -z-10 opacity-20">
              <svg className="w-full h-full" viewBox="0 0 600 400">
                <circle cx="300" cy="200" r="150" fill="#16a085" fillOpacity="0.3" />
                <path d="M100 300 Q300 100 500 300" stroke="#16a085" strokeWidth="4" fill="none" />
                <circle cx="300" cy="100" r="20" fill="#16a085" />
                <circle cx="100" cy="300" r="20" fill="#16a085" />
                <circle cx="500" cy="300" r="20" fill="#16a085" />
              </svg>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl xl:max-w-screen-2xl mx-auto text-center">
          <motion.h3 {...fadeInUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 tracking-tight">
            Contact Information
          </motion.h3>
          <div className="relative">
            <div className="absolute inset-0 -z-10 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 1400 400">
                <rect x="200" y="200" width="1000" height="200" fill="#16a085" fillOpacity="0.2" />
                <path d="M200 300 Q700 100 1200 300" stroke="#16a085" strokeWidth="6" fill="none" />
              </svg>
            </div>
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {[
                { title: "Email", value: "contact@shigjeta.com", href: "mailto:contact@shigjeta.com", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
                { title: "Phone", value: "+383 (46) 192-921", href: "tel:+38346192921", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg> },
                { title: "Address", value: "Kacanik, Kosovo", href: "#", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center mb-4">{item.icon}</div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p>
                    <a href={item.href} className="text-[#16a085] hover:underline hover:text-[#13876b] transition-colors duration-300">
                      {item.value}
                    </a>
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}